import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { fieldErrors, toApiErrorMessage } from '@/lib/api-client';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { useAuth } from './use-auth';
import { authApi } from './auth-api';
import { changePasswordFormSchema, type ChangePasswordFormValues } from './auth-schemas';
import { usePasswordRulesHint } from './use-password-rules';

/**
 * Reachable two ways: from the user menu by choice, and by forced redirect when
 * `mustChangePassword` is set. The forced case gets an explanatory banner —
 * landing on this page unexplained is disorienting.
 */
export function ChangePasswordPage() {
  const { user, applySession } = useAuth();
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);
  const isForced = user?.mustChangePassword ?? false;
  const passwordHint = usePasswordRulesHint();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(changePasswordFormSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);

    try {
      // The endpoint revokes every session and returns a fresh one, so the
      // caller stays signed in while other devices are logged out.
      const session = await authApi.changePassword(values);
      applySession(session);
      toast.success('Password changed. Other devices have been signed out.');
      void navigate('/', { replace: true });
    } catch (error) {
      const fields = fieldErrors(error);
      const passwordIssues = fields.password ?? fields.newPassword;

      if (passwordIssues?.length) {
        setError('newPassword', { message: passwordIssues.join('. ') });
      } else {
        setFormError(toApiErrorMessage(error));
      }
    }
  });

  return (
    <div className="mx-auto w-full max-w-lg">
      <Card>
        <CardHeader>
          <CardTitle>Change password</CardTitle>
          <CardDescription>
            Changing your password signs you out everywhere else.
          </CardDescription>
        </CardHeader>

        <CardContent>
          {isForced && (
            <Alert variant="warning" className="mb-4">
              <ShieldAlert aria-hidden="true" />
              <AlertDescription>
                Your account uses a temporary password. Set a new one to continue.
              </AlertDescription>
            </Alert>
          )}

          <form onSubmit={(event) => void onSubmit(event)} className="space-y-4" noValidate>
            {formError && (
              <Alert variant="destructive">
                <AlertCircle aria-hidden="true" />
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}

            <FormField label="Current password" error={errors.currentPassword?.message} required>
              {(props) => (
                <Input
                  {...props}
                  {...register('currentPassword')}
                  type="password"
                  autoComplete="current-password"
                  autoFocus
                />
              )}
            </FormField>

            <FormField
              label="New password"
              error={errors.newPassword?.message}
              hint={passwordHint}
              required
            >
              {(props) => (
                <Input
                  {...props}
                  {...register('newPassword')}
                  type="password"
                  autoComplete="new-password"
                />
              )}
            </FormField>

            <FormField label="Confirm new password" error={errors.confirmPassword?.message} required>
              {(props) => (
                <Input
                  {...props}
                  {...register('confirmPassword')}
                  type="password"
                  autoComplete="new-password"
                />
              )}
            </FormField>

            <Button type="submit" className="w-full" loading={isSubmitting}>
              Change password
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
